import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Alert,
} from '@mui/material'
import { Search, Lock, ArrowRight } from 'lucide-react'
import { LOCAL, DEVELOP } from 'constants/env'
import ProfileCard from './components/ProfileCard'
import FreeReport from './components/FreeReport'
import GatedReport from './components/GatedReport'
import EmailGate from './components/EmailGate'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

type Stage = 'input' | 'free' | 'gated'

export default function Analyzer(): JSX.Element {
  const [username, setUsername] = useState('')
  const [stage, setStage] = useState<Stage>('input')
  const [isLoading, setIsLoading] = useState(false)
  const [freeData, setFreeData] = useState<any>(null)
  const [gatedData, setGatedData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAnalyze = async (): Promise<void> => {
    if (!username.trim()) return
    setError(null)
    setIsLoading(true)

    try {
      const response = await fetch(`${config.API_URL}/api/analyze/free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim().replace('@', '') }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error ?? 'Analysis failed')
      }
      setFreeData(result.data)
      setStage('free')
    } catch (err: any) {
      setError(err.message)
      setStage('input')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUnlockGated = async (email: string): Promise<void> => {
    setError(null)
    try {
      const response = await fetch(`${config.API_URL}/api/analyze/gated`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim().replace('@', ''),
          email,
        }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error ?? 'Failed to unlock report')
      }
      setGatedData(result.data.gated ?? result.data)
      setStage('gated')
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', pt: { xs: 4, md: 6 }, pb: 3, px: 2 }}>
        <Typography
          sx={{
            fontSize: { xs: '28px', md: '40px' },
            fontWeight: 800,
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #6366f1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Free Instagram Engagement Analyzer
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px', maxWidth: 500, mx: 'auto' }}>
          See your engagement rate, top followers, and best content — in seconds.
        </Typography>
      </Box>

      {/* Main content */}
      <Box sx={{ width: '100%', maxWidth: 640, px: 2, pb: 8 }}>
        {/* Input */}
        {(stage === 'input' || stage === 'free' || stage === 'gated') && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Enter Instagram username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { handleAnalyze().catch(console.error) } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>@</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                },
              }}
            />
            <Button
              onClick={() => { handleAnalyze().catch(console.error) }}
              disabled={!username.trim() || isLoading}
              startIcon={isLoading ? <CircularProgress size={18} /> : <Search size={18} />}
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:disabled': { opacity: 0.5, color: 'white' },
              }}
            >
              Analyze
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#a855f7', mb: 2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Analyzing @{username.replace('@', '')}...
            </Typography>
          </Box>
        )}

        {/* Free report */}
        {(stage === 'free' || stage === 'gated') && freeData && (
          <>
            <ProfileCard profile={freeData.profile} />
            <FreeReport data={freeData} />
          </>
        )}

        {/* Email gate (shown after free report) */}
        {stage === 'free' && (
          <EmailGate onUnlock={handleUnlockGated} />
        )}

        {/* Gated report */}
        {stage === 'gated' && gatedData && (
          <>
            <GatedReport data={gatedData} />

            {/* CTA */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(99,102,241,0.15) 100%)',
                border: '1px solid rgba(168,85,247,0.3)',
                borderRadius: '16px',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '18px', mb: 1 }}>
                Want to find and engage with new followers like these?
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', mb: 2 }}>
                Nalapod finds relevant content in your niche and helps you engage authentically — on autopilot.
              </Typography>
              <Button
                href="https://app.nalapod.com"
                endIcon={<ArrowRight size={16} />}
                sx={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 4,
                  py: 1.2,
                  fontWeight: 600,
                }}
              >
                Start Free Trial
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
